import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import {
  CustomEventPropertyCondition,
  CustomEventPropertyOperator,
  CustomEventTriggerStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { isPropertyNameValid } from "@redotech/redo-model/external-apis/custom-events/create-custom-event";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  CustomEventPropertyKeysContext,
  PropertyInfo,
} from "@redotech/redo-web/marketing/segments/custom-events/custom-event-property-keys-context";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { PartialWithRequiredType } from "../index";

type PartialCustomEventTriggerStep =
  PartialWithRequiredType<CustomEventTriggerStep>;

const defaultOperatorLabels: Record<CustomEventPropertyOperator, string> = {
  [CustomEventPropertyOperator.EQUALS]: "equals",
  [CustomEventPropertyOperator.CONTAINS]: "contains",
  [CustomEventPropertyOperator.GREATER_THAN]: "greater than",
  [CustomEventPropertyOperator.LESS_THAN]: "less than",
};

const dateOperatorLabels: Partial<Record<CustomEventPropertyOperator, string>> =
  {
    [CustomEventPropertyOperator.GREATER_THAN]: "after",
    [CustomEventPropertyOperator.LESS_THAN]: "before",
  };

function getOperatorLabel(
  operator: CustomEventPropertyOperator,
  propertyType: PropertyInfo["type"] | undefined,
): string {
  if (propertyType === "date") {
    return dateOperatorLabels[operator] ?? defaultOperatorLabels[operator];
  }
  return defaultOperatorLabels[operator];
}

function getAvailableOperators(
  propertyType: PropertyInfo["type"] | undefined,
): CustomEventPropertyOperator[] {
  switch (propertyType) {
    case "string":
      return [
        CustomEventPropertyOperator.EQUALS,
        CustomEventPropertyOperator.CONTAINS,
      ];
    case "number":
      return [
        CustomEventPropertyOperator.EQUALS,
        CustomEventPropertyOperator.GREATER_THAN,
        CustomEventPropertyOperator.LESS_THAN,
      ];
    case "boolean":
      return [CustomEventPropertyOperator.EQUALS];
    case "date":
      return [
        CustomEventPropertyOperator.GREATER_THAN,
        CustomEventPropertyOperator.LESS_THAN,
      ];
    default:
      return Object.values(CustomEventPropertyOperator);
  }
}

function defaultValueForOperatorAndType(
  operator: CustomEventPropertyOperator,
  propertyType: PropertyInfo["type"] | undefined,
): string | number | boolean {
  if (
    operator === CustomEventPropertyOperator.EQUALS &&
    propertyType === "boolean"
  ) {
    return true;
  }
  if (propertyType === "date") {
    return new Date().toISOString().split("T")[0] ?? "";
  }
  if (
    operator === CustomEventPropertyOperator.GREATER_THAN ||
    operator === CustomEventPropertyOperator.LESS_THAN ||
    (operator === CustomEventPropertyOperator.EQUALS &&
      propertyType === "number")
  ) {
    return 0;
  }
  return "";
}

export const CustomEventTriggerInspector = memo(
  function CustomEventTriggerInspector({
    step,
    setStep,
  }: {
    step: PartialCustomEventTriggerStep;
    setStep: (step: PartialCustomEventTriggerStep) => void;
  }) {
    const propertyKeysMap = useContext(CustomEventPropertyKeysContext);
    const eventName = step.eventName ?? "";
    const propertyKeys = propertyKeysMap.get(eventName) ?? [];
    const conditions =
      (step.triggerSpecificFields?.conditions as
        | CustomEventPropertyCondition[]
        | undefined) ?? [];

    const addCondition = () => {
      const newCondition: CustomEventPropertyCondition = {
        property: "",
        operator: CustomEventPropertyOperator.EQUALS,
        value: "",
      };
      setStep({
        ...step,
        triggerSpecificFields: {
          ...step.triggerSpecificFields,
          conditions: [...conditions, newCondition],
        },
      });
    };

    const updateCondition = (
      index: number,
      updated: CustomEventPropertyCondition,
    ) => {
      setStep({
        ...step,
        triggerSpecificFields: {
          ...step.triggerSpecificFields,
          conditions: conditions.map((c, i) => (i === index ? updated : c)),
        },
      });
    };

    const removeCondition = (index: number) => {
      setStep({
        ...step,
        triggerSpecificFields: {
          ...step.triggerSpecificFields,
          conditions: conditions.filter((_, i) => i !== index),
        },
      });
    };

    return (
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Event name
          </Text>
          <Text fontSize="xs" textColor="primary">
            {eventName}
          </Text>
        </Flex>

        <Flex align="center" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Property conditions
          </Text>
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={addCondition}
            size="xs"
            text="Add condition"
          />
        </Flex>

        {conditions.length === 0 && (
          <Text fontSize="xxs" textColor="tertiary">
            Trigger fires for all events with this name. Add conditions to
            filter by property values.
          </Text>
        )}

        {conditions.map((condition, index) => (
          <CustomEventPropertyConditionRow
            condition={condition}
            index={index}
            key={index}
            propertyKeys={propertyKeys}
            removeCondition={() => removeCondition(index)}
            setCondition={(updated) => updateCondition(index, updated)}
          />
        ))}
      </Flex>
    );
  },
);

const CustomEventPropertyConditionRow = memo(
  function CustomEventPropertyConditionRow({
    condition,
    propertyKeys,
    setCondition,
    removeCondition,
  }: {
    condition: CustomEventPropertyCondition;
    index: number;
    propertyKeys: PropertyInfo[];
    setCondition: (condition: CustomEventPropertyCondition) => void;
    removeCondition: () => void;
  }) {
    const propertyType = propertyKeys.find(
      (p) => p.key === condition.property,
    )?.type;
    const availableOperators = getAvailableOperators(propertyType);

    const propertyOptions: RedoListItem<PropertyInfo>[] = propertyKeys.map(
      (p) => ({ id: p.key, type: "text", text: p.key, value: p }),
    );

    const operatorOptions: RedoListItem<CustomEventPropertyOperator>[] =
      availableOperators.map((op) => ({
        id: op,
        type: "text",
        text: getOperatorLabel(op, propertyType),
        value: op,
      }));

    const handlePropertyChange = (info: PropertyInfo) => {
      const newOperators = getAvailableOperators(info.type);
      const operator = newOperators.includes(condition.operator)
        ? condition.operator
        : (newOperators[0] ?? CustomEventPropertyOperator.EQUALS);
      setCondition({
        property: info.key,
        operator,
        value: defaultValueForOperatorAndType(operator, info.type),
      });
    };

    const handleOperatorChange = (operator: CustomEventPropertyOperator) => {
      setCondition({
        ...condition,
        operator,
        value: defaultValueForOperatorAndType(operator, propertyType),
      });
    };

    const propertyNameError =
      !propertyKeys.length &&
      condition.property.length > 0 &&
      !isPropertyNameValid(condition.property)
        ? "Only letters, numbers, spaces, _, - allowed"
        : undefined;

    return (
      <Flex align="flex-start" dir="column" gap="sm">
        <Flex align="center" gap="sm" wrap="wrap">
          {propertyOptions.length > 0 ? (
            <RedoSingleSelectDropdownInput<PropertyInfo>
              fitToAnchor={false}
              options={propertyOptions}
              optionSelected={({ item }) => handlePropertyChange(item.value)}
              placeholder="Property name"
              selectedItem={
                condition.property
                  ? propertyOptions.find((o) => o.id === condition.property)
                  : undefined
              }
              size="xs"
            />
          ) : (
            <RedoTextInput
              errorText={propertyNameError}
              placeholder="Property name"
              setValue={(value) =>
                setCondition({ ...condition, property: value })
              }
              size="sm"
              value={condition.property}
            />
          )}
          <RedoSingleSelectDropdownInput<CustomEventPropertyOperator>
            fitToAnchor={false}
            options={operatorOptions}
            optionSelected={({ item }) => handleOperatorChange(item.value)}
            selectedItem={operatorOptions.find(
              (o) => o.id === condition.operator,
            )}
            size="xs"
          />
          <ConditionValueInput
            condition={condition}
            propertyType={propertyType}
            setCondition={setCondition}
          />
          <RedoButton
            IconLeading={TrashIcon}
            onClick={removeCondition}
            theme="destructive"
          />
        </Flex>
      </Flex>
    );
  },
);

const ConditionValueInput = memo(function ConditionValueInput({
  condition,
  propertyType,
  setCondition,
}: {
  condition: CustomEventPropertyCondition;
  propertyType: PropertyInfo["type"] | undefined;
  setCondition: (condition: CustomEventPropertyCondition) => void;
}) {
  if (
    condition.operator === CustomEventPropertyOperator.EQUALS &&
    propertyType === "boolean"
  ) {
    const boolOptions: RedoListItem<boolean>[] = [
      { id: "true", type: "text", text: "true", value: true },
      { id: "false", type: "text", text: "false", value: false },
    ];
    return (
      <RedoSingleSelectDropdownInput<boolean>
        fitToAnchor={false}
        options={boolOptions}
        optionSelected={({ item }) =>
          setCondition({ ...condition, value: item.value })
        }
        selectedItem={boolOptions.find((o) => o.value === condition.value)}
        size="xs"
      />
    );
  }

  if (propertyType === "date") {
    const dateStr =
      typeof condition.value === "string" && condition.value
        ? (condition.value.split("T")[0] ?? condition.value)
        : (new Date().toISOString().split("T")[0] ?? "");
    let plainDate: Temporal.PlainDate | undefined;
    try {
      plainDate = Temporal.PlainDate.from(dateStr);
    } catch {
      plainDate = undefined;
    }
    return (
      <RedoSingleDatePicker
        buttonSize="sm"
        date={plainDate}
        setDate={(d) => setCondition({ ...condition, value: d.toString() })}
      />
    );
  }

  if (propertyType === "number") {
    return (
      <RedoTextInput
        inputTypeHint="number"
        placeholder="Value"
        setValue={(v) => {
          const num = parseFloat(v);
          setCondition({ ...condition, value: isNaN(num) ? v : num });
        }}
        size="sm"
        value={String(condition.value ?? "")}
      />
    );
  }

  return (
    <RedoTextInput
      placeholder="Value"
      setValue={(v) => setCondition({ ...condition, value: v })}
      size="sm"
      value={String(condition.value ?? "")}
    />
  );
});
